/// <reference types="@angular/localize" />

import { CommonModule, DatePipe } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import {
  APP_INITIALIZER,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  isDevMode,
} from "@angular/core";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  Router,
  RouterModule,
  provideRouter,
  withHashLocation,
  withInMemoryScrolling,
} from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { provideHotToastConfig } from "@ngneat/hot-toast";
import * as Sentry from "@sentry/angular";
import { OAuthModule } from "angular-oauth2-oidc";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { provideEnvironmentNgxMask } from "ngx-mask";
import { GlobalErrorHandler } from "./app/_shared/services/global-error-handler";
import { GoogleAnalyticsService } from "./app/_shared/services/google-analytics.service";
import { default as routes } from "./app/app-routes";
import { AppComponent } from "./app/app.component";
import { environment } from "./environments/environment";
import { AuthInterceptor } from "./interceptors/auth.interceptor";

// Initialize Google Analytics
function initializeGoogleAnalytics(gaService: GoogleAnalyticsService) {
  return () => {
    // Load Google Analytics script dynamically if ID is provided
    if (environment.googleAnalyticsId) {
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`;
      document.head.appendChild(script);

      // Initialize tracking
      gaService.initialize();
    }
  };
}

// TinyMCE 8+ requires a license key or GPL declaration
(window as any).tinymce?.overrideDefaults({ license_key: "gpl", promotion: false });

if (environment.production == true) {
  Sentry.init({
    dsn: "https://a421f7ba5e2e4b2df131c5f04c13af18@o4510827525177344.ingest.de.sentry.io/4510827529437264",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    normalizeDepth: 5,
    integrations: [Sentry.replayIntegration(), Sentry.browserTracingIntegration()],
    replaysSessionSampleRate: 0.01,
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0, //TODO: https://docs.sentry.io/platforms/javascript/session-replay/privacy/#privacy-configuration
    // Alleen requests naar de backend-API krijgen de sentry-trace/baggage-headers,
    // zodat front- en backend-fouten dezelfde trace_id delen (cross-project linking).
    // Afgeleid uit de environment-config (per build), niet hardcoded.
    tracePropagationTargets: [environment.apiUrl, /^https:\/\/[^/]*\.assistonline\.eu\/api/],
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      CommonModule,
      NgbModule,
      RouterModule,
      ServiceWorkerModule.register("ngsw-worker.js", {
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: "registerWhenStable:30000",
      }),
      OAuthModule.forRoot()
    ),
    provideRouter(
      routes,
      withHashLocation(),
      withInMemoryScrolling({ anchorScrolling: "enabled" })
    ),
    DatePipe,
    { provide: "LOGIN_URL", useFactory: getLoginUrl, deps: [] },
    { provide: "BASE_URL", useFactory: getBaseUrl, deps: [] },
    { provide: "PUBLIC_BASE_URL", useFactory: getPublicBaseUrl, deps: [] },
    { provide: "APP_KEY", useFactory: getAppKey, deps: [] },
    { provide: DEFAULT_CURRENCY_CODE, useValue: "EUR" },
    { provide: LOCALE_ID, useValue: "nl-BE" },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGoogleAnalytics,
      deps: [GoogleAnalyticsService],
      multi: true,
    },
    provideHotToastConfig({
      position: "top-center",
      dismissible: true,
      loading: {
        autoClose: false,
      },
      success: {
        autoClose: true,
      },
      error: {
        autoClose: true,
      },
      info: {
        autoClose: true,
      },
      warning: {
        autoClose: true,
      },
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideEnvironmentNgxMask(),
    provideCharts(withDefaultRegisterables()),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
}).catch((err) => console.error(err));

export function getBaseUrl() {
  return environment.apiUrl;
}
export function getLoginUrl() {
  return environment.loginUrl;
}

export function getAppKey() {
  return environment.appKey;
}

export function getPublicBaseUrl() {
  return environment.publicApiUrl;
}
